import { useCallback, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Fade,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';

import { IncidentCodeApi } from '@/api';
import { ConfirmationDialog, DataGrid, DataGridToolbar } from '@/components';
import { DataGridToolbarProps } from '@/components/DataGrid/DataGridToolbar';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { IIncidentCode, IRequiredUnitDetail } from '@/models/incident.code';
import {
  getIncidentCodeDataGridColumns,
  incidentCodeInitialColumnSettings,
} from '@/pages/IncidentCodes/data';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils';

const DATAGRID_NAME = 'incidentCode';

interface DeleteModalState {
  open: boolean;
  codeToDelete: IIncidentCode | null;
  selectedIds?: string[];
  isMultiDelete?: boolean;
}

interface MenuState {
  anchorEl: null | HTMLElement;
  selectedCode: IIncidentCode | null;
}

const IncidentCodeDetailPanel = ({ row }: { row: IIncidentCode }) => {
  if (!row.requiredUnits || row.requiredUnits.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2">
          No required units for this incident code.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        m: 1,
        p: 1,
        backgroundColor: 'transparent',
        boxShadow: 'none',
      }}
    >
      <TableContainer>
        <Table size="small" aria-label="required units">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Unit Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Number of Units
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {row.requiredUnits.map(
              (unit: IRequiredUnitDetail, index: number) => (
                <TableRow key={unit.unitType?._id || `unit-${index}`}>
                  <TableCell component="th" scope="row">
                    {unit.unitType?.name || 'N/A'}
                  </TableCell>
                  <TableCell align="right">{unit.number}</TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export const IncidentCodeDataGrid = () => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { updateToast } = useToastStore();
  const dataGridContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      if (!user?._id) {
        return incidentCodeInitialColumnSettings;
      }
      return (
        getLocalStorageItem({
          key: `${DATAGRID_NAME}-${user._id}`,
          prefix: LocalStoragePrefix.TableSetting,
          shouldParse: true,
        }) || incidentCodeInitialColumnSettings
      );
    },
  );

  const handleColumnSettingsChange = useCallback(
    (settings: DataGridColSettings[]) => {
      setColumnSettings(settings);
      if (user?._id) {
        setLocalStorageItem({
          key: `${DATAGRID_NAME}-${user._id}`,
          prefix: LocalStoragePrefix.TableSetting,
          value: settings,
        });
      }
    },
    [user?._id],
  );

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    open: false,
    codeToDelete: null,
  });

  const [refreshKey, setRefreshKey] = useState(0);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );

  const [menuState, setMenuState] = useState<MenuState>({
    anchorEl: null,
    selectedCode: null,
  });

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    incidentCode: IIncidentCode,
  ) => {
    setMenuState({ anchorEl: event.currentTarget, selectedCode: incidentCode });
  };

  const handleMenuClose = () => {
    setMenuState({ anchorEl: null, selectedCode: null });
  };

  const fetchIncidentCodes = useCallback(
    async (page = 1, limit = 20, filterInput?: string) => {
      let actualSearchTerm = '';
      if (filterInput) {
        try {
          const filterModel = JSON.parse(filterInput);
          if (
            filterModel &&
            Array.isArray(filterModel.quickFilterValues) &&
            filterModel.quickFilterValues.length > 0
          ) {
            actualSearchTerm = filterModel.quickFilterValues.join(' ');
          } else if (
            typeof filterInput === 'string' &&
            !filterInput.startsWith('{')
          ) {
            actualSearchTerm = filterInput;
          }
        } catch (e) {
          actualSearchTerm = filterInput;
        }
      }

      try {
        const params: {
          page: number;
          limit: number;
          search?: string;
          sort?: string;
        } = {
          page,
          limit,
          sort: JSON.stringify({ code: 1 }),
        };

        if (actualSearchTerm) {
          params.search = actualSearchTerm;
        }

        const incidentCodesArray = await IncidentCodeApi.list(params);

        if (!Array.isArray(incidentCodesArray)) {
          updateToast({
            type: ConfirmTypes.ERROR,
            message:
              'Failed to retrieve incident codes due to unexpected API response format.',
            open: true,
          });
          return { rows: [], totalCount: 0, hasNextPage: false };
        }

        const hasNextPage = incidentCodesArray.length === limit;

        const totalCount = Math.max(
          (page - 1) * limit +
            incidentCodesArray.length +
            (hasNextPage ? 1 : 0),
          0,
        );

        return {
          rows: incidentCodesArray,
          totalCount,
          hasNextPage,
        };
      } catch (err: any) {
        updateToast({
          type: ConfirmTypes.ERROR,
          message: err.message || 'Failed to fetch incident codes.',
          open: true,
        });
        return { rows: [], totalCount: 0, hasNextPage: false };
      }
    },
    [updateToast],
  );

  const handleAddNewIncidentCode = () => {
    // Navigate to admin route for adding new incident code
    navigate(
      `/admin/account-configuration/incident-codes/new${window.location.search}`,
    );
  };

  const handleViewAction = () => {
    if (menuState.selectedCode?._id) {
      navigate(
        `/admin/account-configuration/incident-codes/view/${menuState.selectedCode._id}${window.location.search}`,
      );
    }
    handleMenuClose();
  };

  const handleEditAction = () => {
    if (menuState.selectedCode?._id) {
      navigate(
        `/admin/account-configuration/incident-codes/edit/${menuState.selectedCode._id}${window.location.search}`,
      );
    }
    handleMenuClose();
  };

  const handleDeleteAction = () => {
    if (!menuState.selectedCode) return;
    setDeleteModalState({ open: true, codeToDelete: menuState.selectedCode });
    handleMenuClose();
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalState({
      open: false,
      codeToDelete: null,
      selectedIds: undefined,
      isMultiDelete: false,
    });
  };

  const handleDeleteSelected = () => {
    if (selectionModel.length === 0) return;

    const selectedIds = selectionModel.map((id) => String(id));
    setDeleteModalState({
      open: true,
      codeToDelete: null,
      selectedIds,
      isMultiDelete: true,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteModalState.isMultiDelete && deleteModalState.selectedIds) {
        // Delete multiple incident codes
        const deletePromises = deleteModalState.selectedIds.map((id) =>
          IncidentCodeApi.remove(id),
        );

        await Promise.all(deletePromises);

        updateToast({
          type: ConfirmTypes.SUCCESS,
          message: `${deleteModalState.selectedIds.length} Incident Codes have been deleted successfully`,
          open: true,
        });

        // Clear selection after delete
        setSelectionModel([]);
      } else if (deleteModalState.codeToDelete) {
        // Delete single incident code
        await IncidentCodeApi.remove(deleteModalState.codeToDelete._id);

        updateToast({
          type: ConfirmTypes.SUCCESS,
          message: 'Incident Code has been deleted successfully',
          open: true,
        });
      }

      setRefreshKey((prev) => prev + 1);
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        message: err.message || 'Failed to delete incident code(s).',
        open: true,
      });
    } finally {
      handleCloseDeleteModal();
    }
  };

  const columns = useMemo(
    () => getIncidentCodeDataGridColumns({ onOpenMenu: handleOpenMenu }),
    [user?.roles],
  );

  const getDetailPanelContent = useCallback(
    ({ row }: { row: IIncidentCode }) => <IncidentCodeDetailPanel row={row} />,
    [],
  );

  // Custom detail panel expand/collapse icons
  const ExpandIcon = () => (
    <KeyboardArrowDownIcon color="error" sx={{ fontSize: 24 }} />
  );
  const CollapseIcon = () => (
    <KeyboardArrowUpIcon color="error" sx={{ fontSize: 24 }} />
  );

  const getToolbarButtons = useCallback(() => {
    const buttons = [];

    // Add the regular "Add Incident Code" button
    buttons.push(
      <Button
        key="add-incident-code"
        onClick={handleAddNewIncidentCode}
        color="error"
        variant="contained"
        size="medium"
      >
        Add Incident Code
      </Button>,
    );

    return buttons;
  }, []);

  return (
    <Box
      ref={dataGridContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        paddingTop: '14px',
        position: 'relative',
      }}
    >
      <DataGrid
        key={refreshKey}
        name={DATAGRID_NAME}
        persistSearch
        fetchData={fetchIncidentCodes}
        columnSettings={columnSettings}
        setColumnSettings={handleColumnSettingsChange}
        columns={columns}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => 'auto'}
        initialPageSize={50}
        hideFilter={false}
        hideQuickFilter={false}
        filterMode="server"
        rowHeight={36}
        pagination={false}
        sx={{
          '& .MuiDataGrid-detailPanel': {
            backgroundColor: '#EE3E5C0D',
            '& .MuiTableCell-root': {
              color: theme.palette.text.primary,
            },
            '& .MuiTypography-root': {
              color: theme.palette.text.primary,
            },
          },
          height: '100%',
          width: '100%',
          '& .MuiDataGrid-main': {
            overflow: 'auto',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflow: 'auto',
          },
          '& .MuiDataGrid-toolbarContainer': {
            zIndex: 2,
            position: 'relative',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row': {
            alignItems: 'center',
          },
          '& .MuiDataGrid-row--detailPanelExpanded': {
            backgroundColor:
              theme.palette.mode === 'light'
                ? theme.palette.error.light
                : theme.palette.background.paper,
            color: theme.palette.text.primary,
            '& .MuiDataGrid-cell': {
              color: theme.palette.text.primary,
            },
          },
          '& .MuiDataGrid-row--detailPanelExpanded:hover': {
            backgroundColor:
              theme.palette.mode === 'light'
                ? theme.palette.error.light
                : theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
        checkboxSelection={true}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        slots={{
          detailPanelExpandIcon: ExpandIcon,
          detailPanelCollapseIcon: CollapseIcon,
          toolbar: DataGridToolbar,
        }}
        slotProps={{
          toolbar: {
            cta: getToolbarButtons(),
            hideFilter: false,
            hideQuickFilter: false,
          } as DataGridToolbarProps,
        }}
        hiddenColumnCount={1}
      />

      {/* Floating delete button centered to the DataGrid */}
      <Fade in={selectionModel.length > 0}>
        <Box
          sx={{
            position: 'absolute',
            bottom: 120,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            width: 'fit-content',
          }}
        >
          <Paper
            elevation={6}
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#0A1929',
              minWidth: '500px',
            }}
          >
            <Button
              onClick={handleDeleteSelected}
              variant="text"
              size="large"
              startIcon={<DeleteIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '12px 0 0 12px',
                color: 'white',
                flex: 1,
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Delete Selected Items ({selectionModel.length})
            </Button>
            <Button
              onClick={() => setSelectionModel([])}
              variant="text"
              sx={{
                color: '#ff4d4f',
                py: 1.5,
                px: 2,
                minWidth: 'auto',
                borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              X
            </Button>
          </Paper>
        </Box>
      </Fade>

      <Menu
        anchorEl={menuState.anchorEl}
        open={Boolean(menuState.anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'incident-code-actions-button',
        }}
      >
        <MenuItem onClick={handleViewAction}>View</MenuItem>
        <MenuItem onClick={handleEditAction}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteAction} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
      <ConfirmationDialog
        open={deleteModalState.open}
        title="Confirm Deletion"
        description={
          deleteModalState.isMultiDelete
            ? `Are you sure you want to delete ${deleteModalState.selectedIds?.length} incident codes? This action will not affect existing or past CFS records.`
            : `Are you sure you want to delete this Incident Code (${
                deleteModalState.codeToDelete?.code || ''
              })? This action will not affect existing or past CFS records.`
        }
        cancelText="Cancel"
        confirmText="Delete"
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};
